import React, { useState, useMemo, useEffect } from 'react'
import { CustomerServiceCentre } from '@mobi/component-library/Base/Link'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { Button } from '@mobi/component-library/Base/Buttons'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { DropdownInput } from '@mobi/component-library/Selection&Input/DropdownInput'
import { LoadingSpinner } from '@mobi/component-library/Feedback/LoadingSpinner'
import { getRequiredValidationMessage, routes } from '@mobi/account/Areas/SignUp/Utils'
import { useDispatch, useSelector } from 'react-redux'
import { RadioButton } from '@mobi/component-library/Selection&Input/RadioButton'
import { spacing } from '@mobi/component-library/Foundations'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { PageLayout } from './PersonalDetails.styles'
import {
  RadioLabel,
  RadioGroup,
  RadioOption,
  RadioOptionLabel,
} from './AdditionalInformation.styled'
import {
  getSignUpState,
  setSignupRiskAssessmentAnswerLookupKeys,
  setSignupRiskAssessmentAnswers,
} from '../Store'
import type { RiskAssessmentItem, Occupation } from '@mobi/api-types'
import { useRiskAssessment } from '../Hooks/useRiskAssessment'
import { trackSignUpNeedAssistance } from '../Utils/ga'
import { useApplication } from '../Hooks'
import { SignupError } from './SignupError'

export const AdditionalInformation = () => {
  const dispatch = useDispatch()
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({})
  const [showErrorPopup, setShowErrorPopup] = React.useState(false)

  const { applicationSave, applicationSaveStatus } = useApplication({
    setValidationMessages,
    setShowErrorPopup,
    nextPageRoute: routes.verificationOptions,
  })
  const isApplicationSavePending = applicationSaveStatus === 'pending'

  const signupState = useSelector(getSignUpState)
  const application = signupState.signupApplication
  const riskAssessmentAnswers = signupState.signupRiskAssessmentAnswers

  const {
    data: riskAssessmentData,
    isLoading: isLoadingRiskAssessment,
    isError: isErrorRiskAssessment,
  } = useRiskAssessment()

  // Build dropdown options
  const employmentStatusOptions = useMemo(
    () => buildRiskAssessmentOptions(riskAssessmentData?.employmentStatuses),
    [riskAssessmentData?.employmentStatuses]
  )
  const occupationOptions = useMemo(() => {
    const selectedEmploymentStatus = riskAssessmentData?.employmentStatuses.find(
      status => getRiskItemKey(status) === riskAssessmentAnswers.employmentStatus
    )
    return buildOccupationOptions(
      riskAssessmentData?.occupations,
      selectedEmploymentStatus?.industryGrouping
    )
  }, [
    riskAssessmentData?.occupations,
    riskAssessmentData?.employmentStatuses,
    riskAssessmentAnswers.employmentStatus,
  ])
  const industryParticipantOptions = useMemo(
    () => buildRiskAssessmentOptions(riskAssessmentData?.industryParticipation),
    [riskAssessmentData?.industryParticipation]
  )
  const bettingCustomerTypeOptions = useMemo(
    () => buildRiskAssessmentOptions(riskAssessmentData?.bettingCustomerTypes),
    [riskAssessmentData?.bettingCustomerTypes]
  )

  // Auto-select occupation if there's only one option
  useEffect(() => {
    if (occupationOptions.length === 1) {
      dispatch(
        setSignupRiskAssessmentAnswers({
          ...riskAssessmentAnswers,
          occupation: occupationOptions[0].value,
        })
      )
    }
  }, [occupationOptions])

  // Loading and Error States
  if (isLoadingRiskAssessment)
    return (
      <PageLayout>
        <Flex justifyContent='center' alignItems='center'>
          <LoadingSpinner />
        </Flex>
      </PageLayout>
    )
  if (isErrorRiskAssessment)
    return (
      <InfoBox color='red' size='md'>
        <b>Additional information is not currently available</b>
        <div>
          Please try again in a few minutes. If the issue persists,
          <CustomerServiceCentre onClickPhoneNumber={trackSignUpNeedAssistance} />
        </div>
      </InfoBox>
    )

  const allowToProceed =
    !!riskAssessmentAnswers.occupation &&
    !!riskAssessmentAnswers.employmentStatus &&
    !!riskAssessmentAnswers.industryParticipant &&
    !!riskAssessmentAnswers.bettingCustomerType

  const onSubmit = () => {
    if (!allowToProceed || !riskAssessmentData) {
      return
    }

    // Transform keys to RiskAssessmentItem objects
    const selectedEmploymentStatus = riskAssessmentData.employmentStatuses.find(
      item => getRiskItemKey(item) === riskAssessmentAnswers.employmentStatus
    )
    const selectedOccupationEntry = riskAssessmentData.occupations.find(
      item => getRiskItemKey(item) === riskAssessmentAnswers.occupation
    )
    const selectedIndustryParticipants = riskAssessmentData.industryParticipation.filter(
      item => getRiskItemKey(item) === riskAssessmentAnswers.industryParticipant
    )
    const selectedBettingCustomerTypes = riskAssessmentData.bettingCustomerTypes.filter(
      item => getRiskItemKey(item) === riskAssessmentAnswers.bettingCustomerType
    )

    if (!selectedEmploymentStatus || !selectedOccupationEntry) {
      return
    }

    dispatch(
      setSignupRiskAssessmentAnswerLookupKeys({
        employmentStatus: selectedEmploymentStatus.lookupKey,
        employmentOccupation: selectedOccupationEntry.lookupKey,
        industryParticipantType: selectedIndustryParticipants.map(i => i.lookupKey),
        bettingCustomerType: selectedBettingCustomerTypes.map(i => i.lookupKey),
      })
    )

    applicationSave({})
  }

  const resetFieldValidation = (field: keyof typeof validationMessages) =>
    setValidationMessages(prev => ({
      ...prev,
      [field]: '',
    }))

  if (!(application.applicationReferenceNumber > 0)) {
    return null
  }

  return (
    <>
      <PageLayout>
        <AccountHeader
          alignItems='start'
          title='A few more questions'
          style={{ marginBottom: 0 }}
        />
        <Flex flexDirection='column' gap={spacing.md} minWidth='0'>
          <DropdownInput
            name='employmentStatus'
            label='What is your employment status?'
            placeholder='What is your employment status?'
            options={employmentStatusOptions}
            value={riskAssessmentAnswers.employmentStatus}
            onChange={value => {
              dispatch(
                setSignupRiskAssessmentAnswers({
                  ...riskAssessmentAnswers,
                  employmentStatus: value,
                  occupation: '',
                })
              )
              resetFieldValidation('employmentStatus')
              resetFieldValidation('occupation')
            }}
            onBlur={() =>
              setValidationMessages(prev => ({
                ...prev,
                employmentStatus: getRequiredValidationMessage(
                  riskAssessmentAnswers.employmentStatus ? 'valid' : ''
                ),
              }))
            }
            isInvalid={!!validationMessages.employmentStatus}
            isDisabled={isApplicationSavePending}
            helpText={validationMessages.employmentStatus}
          />

          {occupationOptions.length > 1 && (
            <DropdownInput
              name='occupation'
              label='Please provide more information'
              placeholder='Search for your primary occupation'
              options={occupationOptions}
              value={riskAssessmentAnswers.occupation}
              onChange={value => {
                dispatch(
                  setSignupRiskAssessmentAnswers({ ...riskAssessmentAnswers, occupation: value })
                )
                resetFieldValidation('occupation')
              }}
              onBlur={() =>
                setValidationMessages(prev => ({
                  ...prev,
                  occupation: getRequiredValidationMessage(
                    riskAssessmentAnswers.occupation ? 'valid' : ''
                  ),
                }))
              }
              isInvalid={!!validationMessages.occupation}
              isDisabled={isApplicationSavePending}
              helpText={validationMessages.occupation}
              searchable={occupationOptions.length > 5}
            />
          )}

          <div>
            <RadioLabel id='industry-participant-label'>
              Are you in the racing or wagering industry?
            </RadioLabel>
            <RadioGroup role='radiogroup' aria-labelledby='industry-participant-label'>
              {industryParticipantOptions.map(option => {
                const optionId = `industry-participant-${option.value}`
                const isChecked = riskAssessmentAnswers.industryParticipant === option.value

                return (
                  <RadioOption key={option.value}>
                    <RadioButton
                      id={optionId}
                      type='Radio'
                      isChecked={isChecked}
                      isDisabled={isApplicationSavePending}
                      onClick={() => {
                        dispatch(
                          setSignupRiskAssessmentAnswers({
                            ...riskAssessmentAnswers,
                            industryParticipant: option.value,
                          })
                        )
                        resetFieldValidation('industryParticipant')
                      }}
                    />
                    <RadioOptionLabel htmlFor={optionId}>{option.label}</RadioOptionLabel>
                  </RadioOption>
                )
              })}
            </RadioGroup>
          </div>

          <div>
            <RadioLabel id='betting-customer-type-label'>
              How do you plan to use your TABtouch account?
            </RadioLabel>
            <RadioGroup role='radiogroup' aria-labelledby='betting-customer-type-label'>
              {bettingCustomerTypeOptions.map(option => {
                const optionId = `betting-customer-type-${option.value}`
                const isChecked = riskAssessmentAnswers.bettingCustomerType === option.value

                return (
                  <RadioOption key={option.value}>
                    <RadioButton
                      id={optionId}
                      type='Radio'
                      isChecked={isChecked}
                      isDisabled={isApplicationSavePending}
                      onClick={() => {
                        dispatch(
                          setSignupRiskAssessmentAnswers({
                            ...riskAssessmentAnswers,
                            bettingCustomerType: option.value,
                          })
                        )
                        resetFieldValidation('bettingCustomerType')
                      }}
                    />
                    <RadioOptionLabel htmlFor={optionId}>{option.label}</RadioOptionLabel>
                  </RadioOption>
                )
              })}
            </RadioGroup>
          </div>
        </Flex>

        <footer>
          <Button
            isFullWidth
            disabled={!allowToProceed}
            isLoading={isApplicationSavePending}
            onClick={onSubmit}
          >
            Next
          </Button>
        </footer>
      </PageLayout>
      <SignupError isOpen={showErrorPopup} onClose={() => setShowErrorPopup(false)} />
    </>
  )
}

/**
 * Helpers
 */

const getRiskItemKey = (item: RiskAssessmentItem): string =>
  `${item.lookupKey.creationSystem}:${item.lookupKey.creationKey}`

const buildOccupationOptions = (
  occupations: Occupation[] | undefined,
  industryGrouping: string | undefined
): { value: string; label: string; subText: string }[] => {
  if (!occupations || !industryGrouping) return []

  const filteredOccupations = industryGrouping
    ? occupations.filter(occ => occ.industryGrouping === industryGrouping)
    : occupations

  return filteredOccupations.map(occ => ({
    value: getRiskItemKey(occ),
    label: occ.name,
    subText: occ.industryName,
  }))
}

const buildRiskAssessmentOptions = (
  items: RiskAssessmentItem[] | undefined
): { value: string; label: string }[] => {
  return (
    items?.map(item => ({
      value: getRiskItemKey(item),
      label: item.name,
    })) ?? []
  )
}
